import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CircleMarker,
  GeoJSON,
  MapContainer,
  Polygon,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import hvtData from "../pages/data/hvt.json";

const theme = {
  ink: "#10233f",
  slate: "#51627a",
  border: "#c8d9ee",
  panel: "#f7fbff",
  panelStrong: "#eff6ff",
  blue: "#2563eb",
  cyan: "#0f766e",
  amber: "#d97706",
  gold: "#d4a017",
  red: "#c2410c",
  rose: "#be123c",
  green: "#15803d",
  white: "#ffffff",
};

const regionCountyMap = {
  "Central Utah": ["Juab", "Millard", "Sanpete", "Sevier"],
  "Western Utah Corridor": ["Box Elder", "Tooele"],
  "Wasatch Front": ["Cache", "Davis", "Salt Lake", "Utah", "Weber"],
  "Eastern Utah": ["Carbon", "Duchesne", "Emery", "Grand", "Uintah"],
  "Southwest Utah": ["Beaver", "Garfield", "Iron", "Kane", "Washington"],
};

const zoneContext = {
  "Central Utah": {
    matchers: ["Central Utah", "Delta-Clover corridor"],
    zoneLabel: "Central Utah",
    counties: "Juab, Millard, Sanpete, and Sevier counties",
    proximity: "Immediate access to Delta, Clover, Mona, and major 500 kV/HVDC corridors.",
    infrastructure: "Delta HVDC hub, Clover, Mona",
    corridorIds: ["hvt-egs-500", "hvt-cross-tie-500", "hvt-twe-500", "hvt-path27-hvdc", "hvt-zephyr-hvdc"],
    substationNames: ["Clover", "Mona", "Delta"],
  },
  "Western Utah Corridor": {
    matchers: ["Western Utah Corridor", "Western Utah"],
    zoneLabel: "Western Utah Corridor",
    counties: "Box Elder and Tooele counties",
    proximity: "Strong western siting option near interties and export-capable infrastructure.",
    infrastructure: "Clover-adjacent western intertie access",
    corridorIds: ["hvt-cross-tie-500"],
    substationNames: ["Clover"],
  },
  "Wasatch Front": {
    matchers: ["Wasatch Front", "SLC-Provo"],
    zoneLabel: "Wasatch Front",
    counties: "Cache, Davis, Salt Lake, Utah, and Weber counties",
    proximity: "Best workforce and fiber access, with stronger congestion risk than central Utah.",
    infrastructure: "Mona and northern 345 kV / 230 kV urban grid access",
    corridorIds: ["hvt-egs-500"],
    substationNames: ["Mona"],
  },
  "Eastern Utah": {
    matchers: ["Eastern Utah", "Uintah", "Carbon"],
    zoneLabel: "Eastern Utah",
    counties: "Carbon, Duchesne, Emery, Grand, and Uintah counties",
    proximity: "Close to energy production areas and renewable corridors, but large-load delivery may need upgrades.",
    infrastructure: "Carbon hub and Uintah Basin node",
    corridorIds: ["hvt-twe-500"],
    substationNames: ["Carbon", "Uintah/Roosevelt"],
  },
  "Southwest Utah": {
    matchers: ["Southwest Utah"],
    zoneLabel: "Southwest Utah",
    counties: "Beaver, Garfield, Iron, Kane, and Washington counties",
    proximity: "Good land and solar profile, but weaker existing high-voltage fit for very large campuses.",
    infrastructure: "Sigurd and Red Butte regional grid access",
    corridorIds: ["hvt-srb-345", "hvt-zephyr-hvdc"],
    substationNames: ["Sigurd", "Red Butte"],
  },
};

const corridorGeoData = [
  {
    id: "hvt-egs-500",
    name: "Energy Gateway South",
    voltageKv: 500,
    capacityMw: 1000,
    capacityLabel: "Multi-GW",
    operator: "PacifiCorp",
    status: "Operational",
    path: [
      [41.4, -111.95],
      [40.35, -111.35],
      [39.82, -111.85],
      [39.58, -111.95],
    ],
  },
  {
    id: "hvt-srb-345",
    name: "Sigurd-Red Butte Line",
    voltageKv: 345,
    capacityMw: 300,
    capacityLabel: "Regional",
    operator: "Regional transmission owners",
    status: "Operational",
    path: [
      [38.85, -111.97],
      [38.2, -112.55],
      [37.65, -113.05],
      [37.15, -113.55],
    ],
  },
  {
    id: "hvt-cross-tie-500",
    name: "Cross-Tie Transmission Line",
    voltageKv: 500,
    capacityMw: 1500,
    capacityLabel: "1,500 MW",
    operator: "TransCanyon",
    status: "Planned",
    path: [
      [39.58, -111.95],
      [39.45, -112.75],
      [39.2, -113.55],
      [38.95, -114.05],
    ],
  },
  {
    id: "hvt-twe-500",
    name: "TransWest Express",
    voltageKv: 500,
    capacityMw: 3000,
    capacityLabel: "3,000 MW",
    operator: "TransWest Express LLC",
    status: "Under construction",
    path: [
      [41.0, -109.6],
      [40.45, -110.35],
      [39.85, -111.2],
      [39.35, -112.58],
    ],
  },
  {
    id: "hvt-path27-hvdc",
    name: "Intermountain HVDC (Path 27)",
    voltageKv: 500,
    capacityMw: 2400,
    capacityLabel: "2,400 MW",
    operator: "Intermountain Power Agency",
    status: "Operational",
    path: [
      [39.35, -112.58],
      [38.8, -113.1],
      [38.0, -113.65],
      [37.35, -114.05],
    ],
  },
  {
    id: "hvt-zephyr-hvdc",
    name: "Zephyr HVDC Line",
    voltageKv: 500,
    capacityMw: 1000,
    capacityLabel: "High capacity",
    operator: "Zephyr project sponsors",
    status: "Under construction",
    path: [
      [38.15, -114.05],
      [38.55, -113.45],
      [39.35, -112.58],
    ],
  },
];

const substations = [
  { name: "Clover", type: "500/345 kV hub", point: [39.58, -111.95], capacity: "Multi-GW convergence" },
  { name: "Mona", type: "345-500 kV node", point: [39.82, -111.85], capacity: "Backbone node" },
  { name: "Delta", type: "+/-500 kV HVDC", point: [39.35, -112.58], capacity: "2,400 MW" },
  { name: "Sigurd", type: "345 kV node", point: [38.85, -111.97], capacity: "Regional backbone" },
  { name: "Red Butte", type: "345 kV node", point: [37.15, -113.55], capacity: "Southern connection" },
  { name: "Carbon", type: "345 kV hub", point: [39.68, -110.85], capacity: "Growing capacity" },
  { name: "Uintah/Roosevelt", type: "230 kV node", point: [40.3, -109.99], capacity: "Moderate" },
];

const cleanText = (value) => {
  if (typeof value !== "string") return value;
  return value
    .replaceAll("â†’", "->")
    .replaceAll("â€“", "-")
    .replaceAll("â‰¤", "<=")
    .replaceAll("Â±", "+/-")
    .replaceAll("Â", "");
};

const normalizeRegionName = (label = "") => {
  if (label.includes("Central Utah")) return "Central Utah";
  if (label.includes("Western Utah")) return "Western Utah Corridor";
  if (label.includes("Wasatch Front")) return "Wasatch Front";
  if (label.includes("Eastern Utah") || label.includes("Uintah") || label.includes("Carbon")) return "Eastern Utah";
  if (label.includes("Southwest Utah")) return "Southwest Utah";
  return label;
};

const getZoneColor = (score) => {
  if (score >= 5) return "#15803d";
  if (score >= 4) return "#2563eb";
  if (score >= 3) return "#d97706";
  return "#c2410c";
};

const getStatusVisual = (status) => {
  if (status === "Shovel-ready") {
    return { text: "Shovel-ready", bg: "#dcfce7", fg: "#166534", border: "#86efac" };
  }
  return { text: "In development", bg: "#ffedd5", fg: "#9a3412", border: "#fdba74" };
};

const getCorridorColor = (voltageKv) => {
  if (voltageKv >= 500) return theme.blue;
  if (voltageKv >= 345) return theme.gold;
  return theme.cyan;
};

const inferAvailableMw = (readiness) => {
  if (!readiness) return { numeric: 0, label: "Unknown" };
  if (readiness["1 GW Ready"] === "Yes") return { numeric: 1000, label: "1,000+ MW available" };
  if (readiness["1 GW Ready"] === "Partial") return { numeric: 700, label: "Up to 1,000 MW with upgrades" };
  if (readiness["300 MW Ready"] === "Yes") return { numeric: 300, label: "300+ MW available" };
  if (readiness["300 MW Ready"] === "Partial") return { numeric: 200, label: "100-300 MW, expansion likely" };
  if (readiness["100 MW Ready"] === "Yes") return { numeric: 100, label: "100+ MW available" };
  if (readiness["100 MW Ready"] === "Partial") return { numeric: 75, label: "Sub-100 MW, upgrade path needed" };
  return { numeric: 50, label: "Below 100 MW today" };
};

const inferStatus = (readiness) => {
  if (!readiness) return "In development";
  return readiness["100 MW Ready"] === "Yes" ? "Shovel-ready" : "In development";
};

const getLatLngsFromGeometry = (geometry) => {
  if (!geometry) return [];
  if (geometry.type === "Polygon") {
    return geometry.coordinates.map((ring) => ring.map(([lng, lat]) => [lat, lng]));
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.map((polygon) =>
      polygon.map((ring) => ring.map(([lng, lat]) => [lat, lng]))
    );
  }
  return [];
};

const flattenLatLngs = (positions) => {
  if (!Array.isArray(positions)) return [];
  if (typeof positions[0] === "number") return [positions];
  return positions.flatMap((value) => flattenLatLngs(value));
};

const getBoundsFromPositions = (positions) => {
  const points = flattenLatLngs(positions);
  if (!points.length) return null;

  let minLat = points[0][0];
  let maxLat = points[0][0];
  let minLng = points[0][1];
  let maxLng = points[0][1];

  points.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });

  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
};

const getCenterFromBounds = (bounds) => {
  if (!bounds) return [39.3, -111.7];
  return [
    (bounds[0][0] + bounds[1][0]) / 2,
    (bounds[0][1] + bounds[1][1]) / 2,
  ];
};

const buildZipBounds = (geometry) => {
  const positions = getLatLngsFromGeometry(geometry);
  return getBoundsFromPositions(positions);
};

const MapViewportController = ({ target }) => {
  const map = useMap();

  useEffect(() => {
    if (!target) return;
    if (target.bounds) {
      map.flyToBounds(target.bounds, { padding: [28, 28], duration: 1.2 });
      return;
    }
    if (target.center) {
      map.flyTo(target.center, target.zoom || 8, { duration: 1.2 });
    }
  }, [map, target]);

  return null;
};

const FilterChip = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full border px-3 py-1.5 text-xs font-bold transition-colors"
    style={{
      borderColor: active ? theme.blue : theme.border,
      background: active ? "#dbeafe" : "#ffffff",
      color: active ? "#1d4ed8" : theme.ink,
    }}
  >
    {children}
  </button>
);

const ProjectReadyMap = () => {
  const data = hvtData.HVT;
  const readinessData =
    data["Transmission Capacity vs Data Center Demand"][
      "Data Center Readiness Layer (Overlay Attribute Table) Utah Fit Analysis"
    ];
  const [countyGeo, setCountyGeo] = useState(null);
  const [zipGeo, setZipGeo] = useState(null);
  const [zipLoading, setZipLoading] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState("Central Utah");
  const [mapTarget, setMapTarget] = useState(null);
  const [capacityFilter, setCapacityFilter] = useState("all");
  const [voltageFilter, setVoltageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [searchFeature, setSearchFeature] = useState(null);
  const [showCorridors, setShowCorridors] = useState(true);
  const [showSubstations, setShowSubstations] = useState(true);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const canvasRenderer = useMemo(() => L.canvas({ padding: 0.5 }), []);
  const searchInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadBoundary = async () => {
      const response = await fetch("/boundary.geojson");
      const json = await response.json();
      if (!cancelled) {
        setCountyGeo(json);
      }
    };

    loadBoundary().catch(() => {
      if (!cancelled) {
        setCountyGeo({ type: "FeatureCollection", features: [] });
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const ensureZipData = async () => {
    if (zipGeo) return zipGeo;
    setZipLoading(true);
    try {
      const response = await fetch("/zipCodeArea.geojson");
      const json = await response.json();
      setZipGeo(json);
      return json;
    } finally {
      setZipLoading(false);
    }
  };

  const normalizedZones = useMemo(() => {
    const readinessLookup = Object.fromEntries(
      readinessData.map((item) => [normalizeRegionName(cleanText(item.Region)), item])
    );

    return data["Regional Transmission Capacity"]
      .map((region) => {
        const zoneId = normalizeRegionName(cleanText(region.Region));
        const readiness = readinessLookup[zoneId];
        const context = zoneContext[zoneId];
        const available = inferAvailableMw(readiness);
        const voltageClass = cleanText(region["Voltage Access"]);
        const maxVoltageKv = Math.max(...(voltageClass.match(/\d+/g) || ["0"]).map(Number));
        const relatedCorridors = corridorGeoData.filter((corridor) =>
          context?.corridorIds?.includes(corridor.id)
        );
        const relatedSubstations = substations.filter((station) =>
          context?.substationNames?.includes(station.name)
        );

        return {
          id: zoneId,
          regionName: cleanText(region.Region),
          zoneLabel: context?.zoneLabel || zoneId,
          score: Number(region["AI/Data Center Score"]) || 0,
          voltageLabel: voltageClass,
          maxVoltageKv,
          capacityAvailability: cleanText(region["Capacity Availability"]),
          gridConstraint: cleanText(region["Grid Constraint Level"]),
          readiness,
          availableMwLabel: available.label,
          availableMwValue: available.numeric,
          status: inferStatus(readiness),
          counties: context?.counties || "Planning-grade county grouping",
          proximity: context?.proximity || "Existing infrastructure context not specified",
          infrastructure: context?.infrastructure || "Regional high-voltage access",
          corridorNames: relatedCorridors.map((corridor) => corridor.name),
          substationNames: relatedSubstations.map((station) => station.name),
          color: getZoneColor(Number(region["AI/Data Center Score"]) || 0),
        };
      })
      .sort((a, b) => b.availableMwValue - a.availableMwValue || b.score - a.score);
  }, [data, readinessData]);

  const countiesByName = useMemo(() => {
    if (!countyGeo?.features) return {};
    return countyGeo.features.reduce((acc, feature) => {
      acc[feature.properties.name] = feature;
      return acc;
    }, {});
  }, [countyGeo]);

  const zonesWithGeometry = useMemo(() => {
    return normalizedZones
      .map((zone) => {
        const countyNames = regionCountyMap[zone.id] || [];
        const countyFeatures = countyNames
          .map((countyName) => countiesByName[countyName])
          .filter(Boolean);
        const positions = countyFeatures.map((feature) => getLatLngsFromGeometry(feature.geometry));
        const bounds = getBoundsFromPositions(positions);
        const center = getCenterFromBounds(bounds);
        return {
          ...zone,
          countyFeatures,
          positions,
          bounds,
          center,
        };
      })
      .filter((zone) => zone.positions.length);
  }, [countiesByName, normalizedZones]);

  const selectedZone =
    zonesWithGeometry.find((zone) => zone.id === selectedZoneId) || zonesWithGeometry[0] || null;

  const filteredZones = useMemo(() => {
    return zonesWithGeometry.filter((zone) => {
      const matchesCapacity =
        capacityFilter === "all" ||
        (capacityFilter === "100" && zone.availableMwValue >= 100) ||
        (capacityFilter === "300" && zone.availableMwValue >= 300) ||
        (capacityFilter === "1000" && zone.availableMwValue >= 1000);

      const matchesVoltage =
        voltageFilter === "all" ||
        (voltageFilter === "230" && zone.maxVoltageKv >= 230) ||
        (voltageFilter === "345" && zone.maxVoltageKv >= 345) ||
        (voltageFilter === "500" && zone.maxVoltageKv >= 500);

      const matchesStatus = statusFilter === "all" || zone.status === statusFilter;
      const matchesRegion = regionFilter === "all" || zone.id === regionFilter;

      return matchesCapacity && matchesVoltage && matchesStatus && matchesRegion;
    });
  }, [capacityFilter, regionFilter, statusFilter, voltageFilter, zonesWithGeometry]);

  const filteredZoneIds = useMemo(
    () => new Set(filteredZones.map((zone) => zone.id)),
    [filteredZones]
  );

  const visibleCorridors = useMemo(() => {
    const zoneCorridorIds = new Set(
      filteredZones.flatMap((zone) =>
        corridorGeoData
          .filter((corridor) => zone.corridorNames.includes(corridor.name))
          .map((corridor) => corridor.id)
      )
    );

    return corridorGeoData.filter((corridor) => {
      if (voltageFilter !== "all" && corridor.voltageKv < Number(voltageFilter)) return false;
      if (!filteredZones.length) return false;
      return zoneCorridorIds.has(corridor.id);
    });
  }, [filteredZones, voltageFilter]);

  const visibleSubstations = useMemo(() => {
    const activeNames = new Set(filteredZones.flatMap((zone) => zone.substationNames));
    return substations.filter((station) => activeNames.has(station.name));
  }, [filteredZones]);

  useEffect(() => {
    if (!selectedZone && filteredZones[0]) {
      setSelectedZoneId(filteredZones[0].id);
      setMapTarget({ bounds: filteredZones[0].bounds });
      return;
    }

    if (selectedZone && !filteredZoneIds.has(selectedZone.id) && filteredZones[0]) {
      setSelectedZoneId(filteredZones[0].id);
      setMapTarget({ bounds: filteredZones[0].bounds });
    }
  }, [filteredZoneIds, filteredZones, selectedZone]);

  const handleZoneSelect = (zone) => {
    setSelectedZoneId(zone.id);
    setSearchFeature(null);
    setSearchMessage(`${zone.zoneLabel} selected`);
    setMapTarget({ bounds: zone.bounds });
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setSearchMessage("Enter a region, county, substation, or Utah ZIP code.");
      return;
    }

    const zoneMatch = zonesWithGeometry.find(
      (zone) =>
        zone.zoneLabel.toLowerCase().includes(query) ||
        zone.regionName.toLowerCase().includes(query)
    );
    if (zoneMatch) {
      handleZoneSelect(zoneMatch);
      return;
    }

    const countyMatch = countyGeo?.features?.find((feature) =>
      feature.properties.name.toLowerCase().includes(query)
    );
    if (countyMatch) {
      const bounds = getBoundsFromPositions(getLatLngsFromGeometry(countyMatch.geometry));
      setSearchFeature(countyMatch);
      setSearchMessage(`County match: ${countyMatch.properties.name} County`);
      setMapTarget({ bounds });
      return;
    }

    const substationMatch = substations.find((station) => station.name.toLowerCase().includes(query));
    if (substationMatch) {
      setSearchFeature(null);
      setSearchMessage(`Substation match: ${substationMatch.name}`);
      setMapTarget({ center: substationMatch.point, zoom: 9 });
      return;
    }

    const isZipSearch = /^\d{5}$/.test(query);
    if (isZipSearch) {
      const zipData = await ensureZipData();
      const zipMatch = zipData.features.find((feature) => feature.properties.ZIP5 === query);
      if (zipMatch) {
        const bounds = buildZipBounds(zipMatch.geometry);
        setSearchFeature(zipMatch);
        setSearchMessage(`ZIP match: ${zipMatch.properties.ZIP5} (${cleanText(zipMatch.properties.NAME)})`);
        setMapTarget({ bounds });
        return;
      }
    }

    setSearchFeature(null);
    setSearchMessage("No Utah Project Ready match found for that search.");
  };

  const summary = useMemo(() => {
    const shovelReadyCount = filteredZones.filter((zone) => zone.status === "Shovel-ready").length;
    const hyperscaleCount = filteredZones.filter((zone) => zone.availableMwValue >= 1000).length;
    const avgScore =
      filteredZones.length > 0
        ? (filteredZones.reduce((sum, zone) => sum + zone.score, 0) / filteredZones.length).toFixed(1)
        : "0.0";

    return { shovelReadyCount, hyperscaleCount, avgScore };
  }, [filteredZones]);

  return (
    <section className="rounded-[28px] border bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)]" style={{ borderColor: theme.border }}>
      <div
        className="rounded-t-[28px] px-5 py-6 sm:px-7"
        style={{
          background:
            "linear-gradient(135deg, rgba(16,35,63,0.98) 0%, rgba(29,78,216,0.96) 46%, rgba(15,118,110,0.94) 100%)",
        }}
      >
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-4xl">
            <p className="text-xs font-black uppercase tracking-[0.28em] text-blue-100">Interactive Siting Overlay</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-white sm:text-4xl">
              Project Ready Zones for Data Center and AI Relocation
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-blue-50 sm:text-base">
              Project Ready zones combine high-voltage transmission access, current capacity signals, and readiness for
              100 MW to 1 GW-class campuses. For enterprise and AI site selection, transmission capacity matters because
              it shapes time-to-power, upgrade risk, redundancy, and long-term expansion options.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[430px]">
            {[
              ["Visible zones", String(filteredZones.length).padStart(2, "0")],
              ["Shovel-ready", String(summary.shovelReadyCount).padStart(2, "0")],
              ["Avg. fit score", `${summary.avgScore}/5`],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur"
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-100">{label}</p>
                <p className="mt-2 text-2xl font-black text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-5 p-4 sm:p-6 xl:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="flex min-h-[780px] flex-col gap-4 self-start xl:sticky xl:top-6">
          <div className="rounded-3xl border bg-slate-50 p-4" style={{ borderColor: theme.border }}>
            <p className="text-xs font-black uppercase tracking-[0.24em]" style={{ color: theme.blue }}>
              Search And Filter
            </p>
            <form className="mt-3" onSubmit={handleSearch}>
              <div className="flex gap-2">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Region, county, substation, or ZIP"
                  className="w-full rounded-2xl border px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500"
                  style={{ borderColor: theme.border }}
                />
                <button
                  type="submit"
                  className="rounded-2xl px-4 py-3 text-sm font-black text-white"
                  style={{ backgroundColor: theme.blue }}
                >
                  Find
                </button>
              </div>
            </form>
            <p className="mt-2 min-h-[20px] text-xs font-medium text-slate-500">
              {zipLoading ? "Loading Utah ZIP geography..." : searchMessage}
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Capacity Threshold</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <FilterChip active={capacityFilter === "all"} onClick={() => setCapacityFilter("all")}>All</FilterChip>
                  <FilterChip active={capacityFilter === "100"} onClick={() => setCapacityFilter("100")}>100+ MW</FilterChip>
                  <FilterChip active={capacityFilter === "300"} onClick={() => setCapacityFilter("300")}>300+ MW</FilterChip>
                  <FilterChip active={capacityFilter === "1000"} onClick={() => setCapacityFilter("1000")}>1 GW+</FilterChip>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Voltage Filter</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <FilterChip active={voltageFilter === "all"} onClick={() => setVoltageFilter("all")}>All</FilterChip>
                  <FilterChip active={voltageFilter === "230"} onClick={() => setVoltageFilter("230")}>230 kV+</FilterChip>
                  <FilterChip active={voltageFilter === "345"} onClick={() => setVoltageFilter("345")}>345 kV+</FilterChip>
                  <FilterChip active={voltageFilter === "500"} onClick={() => setVoltageFilter("500")}>500 kV</FilterChip>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                  Status
                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="mt-2 w-full rounded-2xl border bg-white px-3 py-3 text-sm font-semibold text-slate-900"
                    style={{ borderColor: theme.border }}
                  >
                    <option value="all">All statuses</option>
                    <option value="Shovel-ready">Shovel-ready</option>
                    <option value="In development">In development</option>
                  </select>
                </label>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                  Region
                  <select
                    value={regionFilter}
                    onChange={(event) => setRegionFilter(event.target.value)}
                    className="mt-2 w-full rounded-2xl border bg-white px-3 py-3 text-sm font-semibold text-slate-900"
                    style={{ borderColor: theme.border }}
                  >
                    <option value="all">All regions</option>
                    {zonesWithGeometry.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.zoneLabel}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <FilterChip active={showCorridors} onClick={() => setShowCorridors((value) => !value)}>
                  Corridors
                </FilterChip>
                <FilterChip active={showSubstations} onClick={() => setShowSubstations((value) => !value)}>
                  Substations
                </FilterChip>
                <button
                  type="button"
                  onClick={() => {
                    setCapacityFilter("all");
                    setVoltageFilter("all");
                    setStatusFilter("all");
                    setRegionFilter("all");
                    setSearchFeature(null);
                    setSearchMessage("Filters reset");
                    searchInputRef.current?.focus();
                  }}
                  className="rounded-full border px-3 py-1.5 text-xs font-bold text-slate-700"
                  style={{ borderColor: theme.border }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border bg-white p-4" style={{ borderColor: theme.border }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Project Ready Zones</p>
                <p className="mt-1 text-sm text-slate-600">Click a zone to zoom and inspect siting detail.</p>
              </div>
              <div className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                {filteredZones.length} listed
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {filteredZones.map((zone) => {
                const statusVisual = getStatusVisual(zone.status);
                const isActive = selectedZone?.id === zone.id;
                return (
                  <button
                    key={zone.id}
                    type="button"
                    onClick={() => handleZoneSelect(zone)}
                    className="w-full rounded-3xl border p-4 text-left transition"
                    style={{
                      borderColor: isActive ? zone.color : theme.border,
                      background: isActive ? `${zone.color}12` : "#ffffff",
                      boxShadow: isActive ? `0 14px 34px ${zone.color}22` : "none",
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-base font-black text-slate-950">{zone.zoneLabel}</p>
                        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          Fit score {zone.score}/5
                        </p>
                      </div>
                      <span
                        className="rounded-full border px-2.5 py-1 text-[11px] font-black"
                        style={{
                          color: statusVisual.fg,
                          background: statusVisual.bg,
                          borderColor: statusVisual.border,
                        }}
                      >
                        {statusVisual.text}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-bold text-slate-900">{zone.availableMwLabel}</span>
                      <span className="h-1 w-1 rounded-full bg-slate-300" />
                      <span className="text-slate-600">{zone.voltageLabel}</span>
                    </div>
                  </button>
                );
              })}

              {filteredZones.length === 0 ? (
                <div className="rounded-3xl border border-dashed p-5 text-sm text-slate-500" style={{ borderColor: theme.border }}>
                  No zones match the current filters.
                </div>
              ) : null}
            </div>
          </div>

        </aside>

        <div className="flex min-h-[780px] flex-col gap-4">
          <div className="grid gap-4">
            <div className="overflow-hidden rounded-[28px] border bg-slate-50" style={{ borderColor: theme.border }}>
              <div className="flex items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: theme.border }}>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Planning Map</p>
                  <p className="mt-1 text-sm text-slate-600">County-based regional overlay with search, zoom, and infrastructure layers.</p>
                </div>
                <div className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-white">
                  Leaflet
                </div>
              </div>

              <div className="relative" style={{ height: "820px" }}>
                <MapContainer
                  center={[39.3, -111.7]}
                  zoom={6.4}
                  minZoom={5.8}
                  preferCanvas
                  scrollWheelZoom
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; OpenStreetMap contributors &copy; CARTO'
                  />

                  <MapViewportController target={mapTarget} />

                  {countyGeo ? (
                    <GeoJSON
                      data={countyGeo}
                      style={() => ({
                        color: "#cbd5e1",
                        weight: 1,
                        fillOpacity: 0,
                      })}
                    />
                  ) : null}

                  {zonesWithGeometry.map((zone) => {
                    const isVisible = filteredZoneIds.has(zone.id);
                    const isActive = selectedZone?.id === zone.id;
                    if (!isVisible) return null;

                    return (
                      <Polygon
                        key={zone.id}
                        positions={zone.positions}
                        renderer={canvasRenderer}
                        eventHandlers={{
                          click: () => handleZoneSelect(zone),
                        }}
                        pathOptions={{
                          color: zone.color,
                          fillColor: zone.color,
                          weight: isActive ? 3 : 2,
                          fillOpacity: isActive ? 0.28 : 0.18,
                          dashArray: isActive ? undefined : "6 4",
                        }}
                      >
                        <Tooltip sticky direction="top">
                          <div className="min-w-[180px]">
                            <p className="font-bold">{zone.zoneLabel}</p>
                            <p>{zone.availableMwLabel}</p>
                            <p>{zone.voltageLabel}</p>
                          </div>
                        </Tooltip>
                        <Popup minWidth={280}>
                          <div className="space-y-3 p-1">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-lg font-black text-slate-950">{zone.zoneLabel}</p>
                                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                                  Project Ready fit score {zone.score}/5
                                </p>
                              </div>
                              <span
                                className="rounded-full border px-2.5 py-1 text-[11px] font-black"
                                style={{
                                  ...(() => {
                                    const visual = getStatusVisual(zone.status);
                                    return {
                                      color: visual.fg,
                                      background: visual.bg,
                                      borderColor: visual.border,
                                    };
                                  })(),
                                }}
                              >
                                {zone.status}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="rounded-2xl bg-slate-50 p-2">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">Available MW</p>
                                <p className="mt-1 font-black text-slate-900">{zone.availableMwLabel}</p>
                              </div>
                              <div className="rounded-2xl bg-slate-50 p-2">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">Voltage</p>
                                <p className="mt-1 font-black text-slate-900">{zone.voltageLabel}</p>
                              </div>
                              <div className="rounded-2xl bg-slate-50 p-2">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">Constraint</p>
                                <p className="mt-1 font-black text-slate-900">{zone.gridConstraint}</p>
                              </div>
                              <div className="rounded-2xl bg-slate-50 p-2">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">Infrastructure</p>
                                <p className="mt-1 font-black text-slate-900">{zone.infrastructure}</p>
                              </div>
                            </div>
                            <div className="rounded-2xl border p-3" style={{ borderColor: theme.border }}>
                              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">Readiness by campus size</p>
                              <div className="mt-2 grid grid-cols-3 gap-2 text-center text-[11px] font-black">
                                {["100 MW Ready", "300 MW Ready", "1 GW Ready"].map((label) => {
                                  const value = zone.readiness?.[label] || "No";
                                  const color =
                                    value === "Yes"
                                      ? { bg: "#dcfce7", fg: "#166534" }
                                      : value === "Partial"
                                      ? { bg: "#ffedd5", fg: "#9a3412" }
                                      : { bg: "#fee2e2", fg: "#991b1b" };
                                  return (
                                    <div key={label} className="rounded-xl px-2 py-2" style={{ background: color.bg, color: color.fg }}>
                                      <span className="block text-[10px] uppercase tracking-[0.12em]">
                                        {label.replace(" Ready", "")}
                                      </span>
                                      {value}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            <p className="text-sm leading-6 text-slate-600">{zone.proximity}</p>
                          </div>
                        </Popup>
                      </Polygon>
                    );
                  })}

                  {showCorridors &&
                    visibleCorridors.map((corridor) => (
                      <Polyline
                        key={corridor.id}
                        positions={corridor.path}
                        renderer={canvasRenderer}
                        pathOptions={{
                          color: getCorridorColor(corridor.voltageKv),
                          weight: corridor.voltageKv >= 500 ? 5 : 3,
                          opacity: 0.82,
                        }}
                        eventHandlers={{
                          click: () =>
                            setSearchMessage(
                              `${corridor.name}: ${corridor.voltageKv} kV, ${corridor.capacityLabel}, ${corridor.status}`
                            ),
                        }}
                      >
                        <Tooltip sticky>
                          <div className="min-w-[180px]">
                            <p className="font-bold">{corridor.name}</p>
                            <p>{corridor.voltageKv} kV</p>
                            <p>{corridor.capacityLabel}</p>
                          </div>
                        </Tooltip>
                        <Popup>
                          <div className="space-y-1 p-1 text-sm">
                            <p className="font-black text-slate-950">{corridor.name}</p>
                            <p><span className="font-bold">Voltage:</span> {corridor.voltageKv} kV</p>
                            <p><span className="font-bold">Capacity:</span> {corridor.capacityLabel}</p>
                            <p><span className="font-bold">Operator:</span> {corridor.operator}</p>
                            <p><span className="font-bold">Status:</span> {corridor.status}</p>
                          </div>
                        </Popup>
                      </Polyline>
                    ))}

                  {showSubstations &&
                    visibleSubstations.map((station) => (
                      <CircleMarker
                        key={station.name}
                        center={station.point}
                        radius={7}
                        renderer={canvasRenderer}
                        pathOptions={{
                          color: theme.ink,
                          fillColor: theme.rose,
                          fillOpacity: 1,
                          weight: 2,
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -8]} opacity={1}>
                          <span className="font-bold">{station.name}</span>
                        </Tooltip>
                        <Popup>
                          <div className="space-y-1 p-1 text-sm">
                            <p className="font-black text-slate-950">{station.name}</p>
                            <p><span className="font-bold">Type:</span> {station.type}</p>
                            <p><span className="font-bold">Capacity signal:</span> {station.capacity}</p>
                          </div>
                        </Popup>
                      </CircleMarker>
                    ))}

                  {searchFeature ? (
                    <GeoJSON
                      data={searchFeature}
                      style={() => ({
                        color: "#7c3aed",
                        weight: 3,
                        fillColor: "#8b5cf6",
                        fillOpacity: 0.08,
                        dashArray: "5 5",
                      })}
                    />
                  ) : null}
                </MapContainer>
                {isLegendOpen ? (
                  <div 
                    className="absolute bottom-6 right-6 z-[1000] w-[340px] rounded-3xl border bg-white/95 p-5 shadow-2xl backdrop-blur-md" 
                    style={{ borderColor: theme.border }}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">Map Legend</p>
                      <button 
                        onClick={() => setIsLegendOpen(false)} 
                        className="text-slate-400 transition hover:text-slate-700"
                        title="Close legend"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="mt-4 flex flex-col gap-3">
                      {[
                        ["#15803d", "Tier 1 fit", "Best current signal for 1 GW-class or large-scale growth"],
                        ["#2563eb", "Tier 2 fit", "Strong siting candidate with good existing backbone"],
                        ["#d97706", "Tier 3 fit", "Usable with selective upgrades or smaller initial loads"],
                        ["#c2410c", "Tier 4 fit", "More likely to require substantial grid development"],
                      ].map(([color, label, text]) => (
                        <div key={label} className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: color }} />
                            <p className="text-xs font-black text-slate-900">{label}</p>
                          </div>
                          <p className="pl-5 text-[10px] font-medium leading-tight text-slate-500">{text}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex flex-col gap-3 border-t pt-4" style={{ borderColor: theme.border }}>
                      <div className="flex items-center gap-3 text-[11px] font-bold text-slate-700">
                        <span className="h-1.5 w-8 rounded-full" style={{ backgroundColor: theme.blue }} />
                        500 kV / HVDC corridor
                      </div>
                      <div className="flex items-center gap-3 text-[11px] font-bold text-slate-700">
                        <span className="h-1.5 w-8 rounded-full" style={{ backgroundColor: theme.gold }} />
                        345 kV corridor
                      </div>
                      <div className="flex items-center gap-3 text-[11px] font-bold text-slate-700">
                        <span className="h-3 w-3 rounded-full border-2 border-slate-800" style={{ backgroundColor: theme.rose }} />
                        Substation / hub
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsLegendOpen(true)}
                    className="absolute bottom-6 right-6 z-[1000] flex items-center gap-2 rounded-full border bg-white px-4 py-2.5 text-sm font-bold shadow-lg transition hover:bg-slate-50"
                    style={{ borderColor: theme.border, color: theme.ink }}
                  >
                    <svg className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                    View Map Legend
                  </button>
                )}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {selectedZone ? (
                <div
                  className="rounded-3xl border bg-gradient-to-br from-slate-50 to-blue-50 p-4 lg:col-span-2"
                  style={{ borderColor: theme.border }}
                >
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Selected Zone</p>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-2xl font-black text-slate-950">{selectedZone.zoneLabel}</h3>
                    <span
                      className="rounded-full px-3 py-1 text-xs font-black text-white"
                      style={{ backgroundColor: selectedZone.color }}
                    >
                      Fit Score {selectedZone.score}/5
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{selectedZone.proximity}</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {[
                      ["Available transmission", selectedZone.availableMwLabel],
                      ["Voltage level", selectedZone.voltageLabel],
                      ["Infrastructure proximity", selectedZone.infrastructure],
                      ["County coverage", selectedZone.counties],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-2xl border bg-white p-3" style={{ borderColor: theme.border }}>
                        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-3xl border bg-white p-4" style={{ borderColor: theme.border }}>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Method Note</p>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Zone business metrics come from the supplied HVT JSON. Map polygons are county-based planning overlays
                  used to make regional siting comparisons searchable and clickable inside the browser.
                </p>
              </div>

              <div className="rounded-3xl border bg-white p-4" style={{ borderColor: theme.border }}>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Quick Stats</p>
                <div className="mt-3 grid gap-3">
                  {[
                    ["Hyperscale-capable zones", String(summary.hyperscaleCount)],
                    ["Visible corridors", String(visibleCorridors.length)],
                    ["Visible substations", String(visibleSubstations.length)],
                    ["Top candidate", filteredZones[0]?.zoneLabel || "None"],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-2xl bg-slate-50 px-3 py-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</p>
                      <p className="mt-1 text-base font-black text-slate-950">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectReadyMap;
